import React, { useEffect, useState } from "react";
import {
  ApolloClient,
  InMemoryCache,
  ApolloProvider,
  createHttpLink,
  from,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { BrowserRouter as Router, Routes } from "react-router-dom";
import { Route, Navigate } from "react-router";
import { setContext } from "@apollo/client/link/context";

import Login from "./pages/Login/Login";
import ResetPassword from "./pages/Login/ResetPassword";
import Home from "./pages/Home/Home";
import Navbar from "./components/Navbar/Navbar";
import Footer from "./components/Footer/Footer";
import QuoteStickyTab from "./components/QuoteStickyTab/QuoteStickyTab";
import GetAQuote from "./pages/GetAQuote/GetAQuote";
// import MeetTheTeam from "./pages/MeetTheTeam/MeetTheTeam";
// ship test
import OurDoors from "./pages/OurDoors/OurDoors";
import Contact from "./pages/Contact/Contact";
// Testimonials removed from the site 2026-07-14 (no V2 page, dropped from sitemap):
// import Testimonials from "./pages/Testimonials/Testimonials";
import Protected from "./components/Protected";
import DesignerPage from "./pages/theme-designer/DesignerPage";
// Public Pages V2 — developer-only preview (not public)
import PublicV2Index from "./pagesV2/PublicV2Index";
import HomeV2 from "./pagesV2/pages/HomeV2";
import QuoteV2 from "./pagesV2/pages/QuoteV2";
import OurDoorsV2 from "./pagesV2/pages/OurDoorsV2";
import ContactV2 from "./pagesV2/pages/ContactV2";
import PrivacyV2 from "./pagesV2/pages/PrivacyV2";
import TermsV2 from "./pagesV2/pages/TermsV2";
import CustomerQuotePageV2 from "./pagesV2/pages/CustomerQuotePageV2";
import CustomerInvoicePageV2 from "./pagesV2/pages/CustomerInvoicePageV2";
// bootstrap + App.css moved to index.js / index.css on 2026-09-21 — see the
// CSS order note in index.js. Nothing global is imported from this file now.
import AdminDashboard from "./pages/AdminDashboard/AdminPages/AdminDasboard/AdminDashboard";
import Auth from "./utils/auth";
import { getGraphQLEndpoint } from "./utils/isTestMode";
import AdminNavbar from "./pages/AdminDashboard/adminComponents/AdminNav/AdminNavbar";
import AdminImageGroups from "./pages/AdminDashboard/AdminPages/ImageGroups/ImageGroups";
import AdminShopInventory from "./pages/AdminDashboard/AdminPages/AdminShopInventory/AdminShopInventory";
import RefinUpdateList from "./pages/AdminDashboard/AdminPages/RefinUpdateList/RefinUpdateList";
import AdminEmails from "./pages/AdminDashboard/AdminPages/EmailPage/Emails";
import Metrics from "./pages/AdminDashboard/AdminPages/Metrics/Metrics";
import SingleClient from "./pages/AdminDashboard/AdminPages/SingleClientPage/SingleClient";
import SentEmailsPage from "./pages/AdminDashboard/AdminPages/SentEmailsPage/SentEmailsPage";
import BlockedEmailsPage from "./pages/AdminDashboard/AdminPages/BlockedEmails/BlockedEmails";
import CustomerQuotePage from "./pages/CustomerQuotePage/CustomerQuotePage";
import ServicesManagement from "./pages/AdminDashboard/AdminPages/ServicesManagement/ServicesManagement";
import ManageUsers from "./pages/AdminDashboard/AdminPages/ManageUsers/ManageUsers";
import WalkthroughsPage from "./pages/AdminDashboard/AdminPages/Walkthroughs/WalkthroughsPage";
import MessagesPage from "./pages/AdminDashboard/AdminPages/MessagesPage/MessagesPage";
import PrivacyPolicy from "./pages/PrivacyPolicy-TOS/PrivacyPolicy";
import TermsOfService from "./pages/PrivacyPolicy-TOS/TermsOfService";
import StripeTransactions from "./pages/AdminDashboard/AdminPages/StripeTransactions/StripeTransactions";
import KeapMergeController from "./pages/AdminDashboard/AdminPages/KeapMergeController/KeapMergeController";
import ShortcutsPage from "./pages/AdminDashboard/AdminPages/ShortcutsPage/ShortcutsPage";
import CustomerInvoicePage from "./pages/CustomerInvoicePage/CustomerInvoicePage";
import KeapFileMerge from "./pages/AdminDashboard/AdminPages/KeapFileMerge/KeapFileMerge";
import MaintenanceReminders from "./pages/AdminDashboard/AdminPages/MaintenanceReminders/MaintenanceReminders";
import TimesheetPage from "./pages/AdminDashboard/AdminPages/Timesheet/TimesheetPage";
import { SSEProvider } from "./utils/SSE/SSEContext";
import GlobalSSEToasts from "./utils/SSE/GlobalSSEToasts";
import DeployRefreshManager from "./utils/DeployRefresh/DeployRefreshManager";
import Emily502Modal from "./utils/EasterEggs/Emily502Modal";
import ClockInReminder from "./utils/ClockInReminder/ClockInReminder";
import { GlobalSnackbarProvider } from "./utils/GlobalSnackbar/GlobalSnackbarContext";
import { GlobalConfirmProvider } from "./utils/GlobalConfirm/GlobalConfirmContext";
import { TourProvider } from "./tours/TourContext";
import { DriveTimePinProvider } from "./utils/DriveTimePin/DriveTimePinContext";
import { isTourActive } from "./tours/tourRuntime";
// import StripePayment from "./pages/AdminDashboard/AdminPages/StripePayment/StripePayment";
const StripePayment = React.lazy(() =>
  import("./pages/AdminDashboard/AdminPages/StripePayment/StripePayment")
);
const JobFormReviewPage = React.lazy(() =>
  import("./pages/AdminDashboard/AdminPages/JobFormReview/JobFormReviewPage")
);
const ReferralsPage = React.lazy(() =>
  import("./pages/AdminDashboard/AdminPages/Referrals/ReferralsPage")
);
const StuckSubmissionsPage = React.lazy(() =>
  import("./pages/AdminDashboard/AdminPages/StuckSubmissions/StuckSubmissionsPage")
);
const JobOptionsSetupPage = React.lazy(() =>
  import("./pages/AdminDashboard/AdminPages/JobOptionsSetup/JobOptionsSetupPage")
);
const ReferredBySetupPage = React.lazy(() =>
  import("./pages/AdminDashboard/AdminPages/ReferredBySetup/ReferredBySetupPage")
);
const BusinessLedgerPage = React.lazy(() =>
  import("./pages/AdminDashboard/AdminPages/BusinessLedger/BusinessLedgerPage")
);
const PayrollReportPage = React.lazy(() =>
  import("./pages/AdminDashboard/AdminPages/PayrollReport/PayrollReportPage")
);
const DefaultsSetupPage = React.lazy(() =>
  import("./pages/AdminDashboard/AdminPages/DefaultsSetup/DefaultsSetupPage")
);
const FcmTokenManager = React.lazy(() =>
  import("./pages/AdminDashboard/AdminPages/FcmTokenManager/FcmTokenManager")
);
const QuickTextManagement = React.lazy(() =>
  import("./pages/AdminDashboard/AdminPages/QuickTextManagement/QuickTextManagement")
);
// Developer-only living style reference (design tokens + component shells).
const StyleGuidePage = React.lazy(() =>
  import("./pages/AdminDashboard/AdminPages/StyleGuidePage/StyleGuidePage")
);
const ClassificationReviewPage = React.lazy(() =>
  import("./pages/AdminDashboard/AdminPages/ClassificationReview/ClassificationReviewPage")
);

const httpLink = createHttpLink({
  uri: getGraphQLEndpoint(),
});

// Construct request middleware that will attach the JWT token to every request as an `authorization` header
const authLink = setContext((_, { headers }) => {
  // get the authentication token from local storage if it exists
  const token = localStorage.getItem("id_token");
  // SSE connectionId for this tab — set by SSEContext on `connected`. Used
  // by the calendar_update self-deliver path so the originating tab can
  // suppress its own snackbar while the user's other tabs still toast.
  // sessionStorage is per-tab, which is exactly what we want.
  const sseConnectionId = sessionStorage.getItem("bdr_sse_connection_id") || "";
  // return the headers to the context so httpLink can read them
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : "",
      "x-bdr-connection-id": sseConnectionId,
      // Feature-tour peek mode (registry §36.4): only while a walkthrough
      // runs, so opening an email / text thread in a tour doesn't mark it read.
      ...(isTourActive() ? { "x-bdr-tour": "1" } : {}),
    },
  };
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
  // The server sets extensions.code = "UNAUTHENTICATED" on every auth
  // failure via the formatError compat layer in server/server.js + the
  // ErrorHandler helper. Branch on the code only — the string-matching
  // fallbacks ("not authenticated", "not logged in", "must be logged in")
  // that used to live here were a workaround for the code not being set.
  //
  // Public/auth routes handle their own errors INLINE — the global
  // session-expiry redirect must NOT fire there. A failed login is coded
  // UNAUTHENTICATED (server.js: "Incorrect Credentials"/"Too Many Attempts"),
  // and the old unconditional redirect hard-reloaded /login-portal on every bad
  // password — wiping the form AND the "Forgot password?" link before the user
  // could reach it. The redirect is still the right behavior on PROTECTED routes
  // (real session expiry). Guard BOTH the token-clear and the redirect together
  // so a public route never lands in a token-cleared-but-not-redirected state.
  // (2026-06-29)
  const path =
    typeof window !== "undefined" ? window.location.pathname : "";
  const onPublicRoute =
    path === "/" ||
    /^\/(login-portal|reset|quote|our-doors|contact-us|testimonials|privacy-policy|terms-of-service|client)(\/|$)/.test(
      path
    );
  const redirectToLogin = () => {
    if (onPublicRoute) return;
    localStorage.removeItem("id_token");
    window.location.href = "/login-portal";
  };
  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      if (err.extensions?.code === "UNAUTHENTICATED") {
        redirectToLogin();
        return;
      }
    }
  }
  if (networkError && networkError.statusCode === 401) {
    redirectToLogin();
  }
});

const client = new ApolloClient({
  link: from([errorLink, authLink, httpLink]),
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          /*
           * `getQuoteRequests` is read by TWO documents on one store key — the
           * Tasks console tile selects `totalDoc`, its modal selects
           * `totalDoc` + `emails`. `IncEmailAndCount` has no `id`, so it is
           * not normalised and the DEFAULT field merge replaces the whole
           * object. Without this, any tile refetch (a reconnect, a remount)
           * writes `{ totalDoc }` over `{ totalDoc, emails }`, evicting the
           * list an open modal is reading and logging "Cache data may be lost
           * when replacing the getQuoteRequests field of a Query object".
           */
          getQuoteRequests: { merge: true },
        },
      },
    },
  }),
});

// GO-LIVE SWITCH (2026-07-14): true serves the V2 public pages at the canonical
// public routes; false instantly restores the V1 public pages (one-line
// rollback, no revert/rebuild). V1 route elements are kept in-file below.
const PUBLIC_V2 = true;

function App() {
  //this admin check is a check is if any user is logged in. Not just admin.
  const [adminIsLoggedIn, setAdminIsLoggedIn] = useState(false);
  const [stickyTab, setStickyTab] = useState(true);
  const [displayFooter, setDisplayFooter] = useState(true);
  const [displayNav, setDisplayNav] = useState(true);

  useEffect(() => {
    const authLogged = Auth.loggedIn();

    if (adminIsLoggedIn !== authLogged) {
      setAdminIsLoggedIn(authLogged);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminIsLoggedIn]);

  const changeLoggedIn = async (newState) => {
    setAdminIsLoggedIn(newState);
    return true;
  };

  const changeStickyTab = (newState) => {
    setStickyTab(newState);
    return;
  };

  const changeFooter = (newState) => {
    setDisplayFooter(newState);
    return;
  };

  const showNav = (bool) => {
    setDisplayNav(bool);
  };

  return (
    <ApolloProvider client={client}>
      <GlobalSnackbarProvider>
      <GlobalConfirmProvider>
      {/* Drive-time pins (registry §17.10): up to two pinned addresses every
          address surface can compare against. Above the Router because the
          navbar chip and the pages both read it; cleared on logout. */}
      <DriveTimePinProvider active={adminIsLoggedIn}>
      <SSEProvider active={adminIsLoggedIn}>
      {/* Always-mounted SSE toast service — fires live-update toasts on every
          route, not just where AdminNavbar is mounted. Gated on login so public
          visitors never mount it (SSE itself is admin/office-only too). */}
      {adminIsLoggedIn && <GlobalSSEToasts />}
      {/* Deploy-warning countdown + stale-tab refresh banner. Mounted for ANY
          logged-in user (adminIsLoggedIn = logged-in check, not role) — the
          SSE lane is inert for non-admin/office, the version-poll lane isn't. */}
      {adminIsLoggedIn && <DeployRefreshManager />}
      {/* Mounted unconditionally: internally inert unless the target user is
          logged in (or ?emily502=preview for local testing). */}
      <Emily502Modal />
      {/* Office-only clock-in reminder: ~60s after an office login, if the user
          has no open shift, offers to clock in. Self-gates on role + a fresh-
          login marker; admins/developers never see it. */}
      {adminIsLoggedIn && <ClockInReminder />}
      <Router>
      {/* Feature tours / What's New / Help (registry §36). Inside the Router
          (it navigates) and around the navbar (which opens Help). Inert
          until logged in. */}
      <TourProvider active={adminIsLoggedIn}>
        {displayNav &&
          (adminIsLoggedIn ? (
            <AdminNavbar changeLoggedIn={changeLoggedIn} />
          ) : (
            <Navbar />
          ))}
        <>
          <Routes>
            <Route
              index
              path=":scanned"
              element={
                PUBLIC_V2 ? (
                  <HomeV2 displayNav={setDisplayNav} changeStickyTab={changeStickyTab} changeFooter={changeFooter} preview={false} />
                ) : (
                  <Home />
                )
              }
            />
            <Route path="/login-portal">
              <Route
                index
                element={<Login changeLoggedIn={changeLoggedIn} />}
              />
              <Route
                path="payment/:paymentID"
                element={<Login changeLoggedIn={changeLoggedIn} />}
              />
            </Route>
            <Route
              index
              path="/reset"
              element={
                <ResetPassword
                  changeLoggedIn={changeLoggedIn}
                  displayNav={setDisplayNav}
                  changeFooter={changeFooter}
                />
              }
            />
            <Route
              index
              path="/quote"
              element={
                PUBLIC_V2 ? (
                  <QuoteV2 displayNav={setDisplayNav} changeStickyTab={changeStickyTab} changeFooter={changeFooter} preview={false} />
                ) : (
                  <GetAQuote />
                )
              }
            />
            {/* Meet the team page endpoint removed. Awaiting correct information */}
            {/* <Route index path="/meet-the-team" element={<MeetTheTeam />} /> */}
            <Route
              index
              path="/our-doors"
              element={
                PUBLIC_V2 ? (
                  <OurDoorsV2 displayNav={setDisplayNav} changeStickyTab={changeStickyTab} changeFooter={changeFooter} preview={false} />
                ) : (
                  <OurDoors />
                )
              }
            />
            <Route
              index
              path="/contact-us"
              element={
                PUBLIC_V2 ? (
                  <ContactV2 displayNav={setDisplayNav} changeStickyTab={changeStickyTab} changeFooter={changeFooter} preview={false} />
                ) : (
                  <Contact displayHeader={true} />
                )
              }
            />
            {/* /testimonials removed 2026-07-14 — no V2 page; also dropped from
                sitemap.xml. Left OUT of the app so nothing links to it and
                crawlers have nothing to pick up. */}
            <Route
              index
              path="/privacy-policy"
              element={
                PUBLIC_V2 ? (
                  <PrivacyV2 displayNav={setDisplayNav} changeStickyTab={changeStickyTab} changeFooter={changeFooter} preview={false} />
                ) : (
                  <PrivacyPolicy />
                )
              }
            />
            <Route
              index
              path="/terms-of-service"
              element={
                PUBLIC_V2 ? (
                  <TermsV2 displayNav={setDisplayNav} changeStickyTab={changeStickyTab} changeFooter={changeFooter} preview={false} />
                ) : (
                  <TermsOfService />
                )
              }
            />

            {/* Public Pages V2 — internal preview for developer/admin/office.
                NOT public; each route is gated by the existing Protected handler
                (permCheck={["developer","admin","office"]}). The v2 layout
                suppresses the global admin nav and restores it on exit
                (PublicV2Layout). */}
            <Route path="/dev-preview">
              <Route
                index
                element={
                  <Protected permCheck={["developer", "admin", "office"]}>
                    <PublicV2Index displayNav={setDisplayNav} changeStickyTab={changeStickyTab} changeFooter={changeFooter} />
                  </Protected>
                }
              />
              <Route
                path="home"
                element={
                  <Protected permCheck={["developer", "admin", "office"]}>
                    <HomeV2 displayNav={setDisplayNav} changeStickyTab={changeStickyTab} changeFooter={changeFooter} preview />
                  </Protected>
                }
              />
              <Route
                path="quote"
                element={
                  <Protected permCheck={["developer", "admin", "office"]}>
                    <QuoteV2 displayNav={setDisplayNav} changeStickyTab={changeStickyTab} changeFooter={changeFooter} preview />
                  </Protected>
                }
              />
              <Route
                path="our-doors"
                element={
                  <Protected permCheck={["developer", "admin", "office"]}>
                    <OurDoorsV2 displayNav={setDisplayNav} changeStickyTab={changeStickyTab} changeFooter={changeFooter} preview />
                  </Protected>
                }
              />
              <Route
                path="contact"
                element={
                  <Protected permCheck={["developer", "admin", "office"]}>
                    <ContactV2 displayNav={setDisplayNav} changeStickyTab={changeStickyTab} changeFooter={changeFooter} preview />
                  </Protected>
                }
              />
              <Route
                path="privacy-policy"
                element={
                  <Protected permCheck={["developer", "admin", "office"]}>
                    <PrivacyV2 displayNav={setDisplayNav} changeStickyTab={changeStickyTab} changeFooter={changeFooter} preview />
                  </Protected>
                }
              />
              <Route
                path="terms-of-service"
                element={
                  <Protected permCheck={["developer", "admin", "office"]}>
                    <TermsV2 displayNav={setDisplayNav} changeStickyTab={changeStickyTab} changeFooter={changeFooter} preview />
                  </Protected>
                }
              />
              <Route
                path="view-quote/:quoteId"
                element={
                  <Protected permCheck={["developer", "admin", "office"]}>
                    <CustomerQuotePageV2 displayNav={setDisplayNav} changeStickyTab={changeStickyTab} changeFooter={changeFooter} />
                  </Protected>
                }
              />
              <Route
                path="view-invoice/:invoiceId"
                element={
                  <Protected permCheck={["developer", "admin", "office"]}>
                    <CustomerInvoicePageV2 displayNav={setDisplayNav} changeStickyTab={changeStickyTab} changeFooter={changeFooter} />
                  </Protected>
                }
              />
            </Route>

            <Route path="/client">
              <Route path="view-quote">
                <Route
                  index={true}
                  path=":quoteId"
                  element={
                    <CustomerQuotePage
                      displayNav={setDisplayNav}
                      changeStickyTab={changeStickyTab}
                      changeFooter={changeFooter}
                    />
                  }
                />
              </Route>
              <Route path="view-invoice">
                <Route
                  index={true}
                  path=":invoiceId"
                  element={
                    <CustomerInvoicePage
                      displayNav={setDisplayNav}
                      changeStickyTab={changeStickyTab}
                      changeFooter={changeFooter}
                    />
                  }
                />
              </Route>
            </Route>

            <Route path="/bdr-office">
              <Route
                index
                path="tasks"
                element={
                  <Protected>
                    <AdminDashboard changeLoggedIn={changeLoggedIn} />
                  </Protected>
                }
              />
              {/* Per-user theming Designer — DEVELOPER-only for now. Lets a
                  user restyle their own BDR app; publishes to their own login. */}
              <Route
                path="theme-designer"
                element={
                  <Protected permCheck={["developer"]}>
                    <DesignerPage />
                  </Protected>
                }
              />
              {/* 2026-05-15 — Canonical route is now /bdr-office/tasks
                  (the page only shows the tasks board, no other
                  "dashboard" content). Keep the legacy /dashboard path
                  as a redirect so existing bookmarks and any external
                  links still land on the right page. */}
              <Route
                path="dashboard"
                element={<Navigate to="/bdr-office/tasks" replace />}
              />
              <Route
                path="img-groups"
                element={
                  <Protected>
                    <AdminImageGroups />
                  </Protected>
                }
              />
              <Route
                path="shop-inventory"
                element={
                  <Protected>
                    <AdminShopInventory />
                  </Protected>
                }
              />
              <Route
                path="maintenance-reminders"
                element={
                  <Protected permCheck={"admin"}>
                    <MaintenanceReminders />
                  </Protected>
                }
              />
              <Route
                path="update-list"
                element={
                  <Protected>
                    <RefinUpdateList />
                  </Protected>
                }
              />
              <Route
                path="email-inbox"
                element={
                  // Emails / Messages / Clients: admin + office only (owner
                  // 2026-09-16). Refinishers never see these in the nav; this
                  // stops a typed URL too. Server gates are unchanged.
                  <Protected permCheck={["admin", "office"]}>
                    <AdminEmails />
                  </Protected>
                }
              />
              <Route
                path="email-sent"
                element={
                  <Protected permCheck={["admin", "office"]}>
                    <SentEmailsPage />
                  </Protected>
                }
              />
              <Route
                path="email-blocked"
                element={
                  <Protected permCheck={["admin", "office"]}>
                    <BlockedEmailsPage />
                  </Protected>
                }
              />
              <Route
                path="messages"
                element={
                  <Protected permCheck={["admin", "office"]}>
                    <MessagesPage />
                  </Protected>
                }
              />
              <Route
                path="metrics"
                element={
                  // Admin/developer only: the Business tab renders 13 years of
                  // company revenue, per-crew splits and top clients. The nav
                  // link has always been inside the admin-only block, but the
                  // route itself had no permCheck, so any logged-in user could
                  // reach it by typing the URL. Server-side enforcement lives in
                  // the getBusinessMetrics resolver — this is the UI half.
                  <Protected permCheck={["developer", "admin"]}>
                    <Metrics />
                  </Protected>
                }
              />
              <Route path="payments">
                <Route
                  path="transactions"
                  element={
                    <Protected>
                      <React.Suspense>
                        <StripeTransactions />
                      </React.Suspense>
                    </Protected>
                  }
                />
                <Route
                  path="process-payment"
                  element={
                    <Protected>
                      <React.Suspense>
                        <StripePayment showNav={showNav} />
                      </React.Suspense>
                    </Protected>
                  }
                >
                  <Route
                    path=":complete"
                    element={
                      <Protected>
                        <React.Suspense>
                          <StripePayment showNav={showNav} />
                        </React.Suspense>
                      </Protected>
                    }
                  />
                  <Route
                    path="payment-id/:paymentId"
                    element={
                      <Protected>
                        <React.Suspense>
                          <StripePayment showNav={showNav} />
                        </React.Suspense>
                      </Protected>
                    }
                  />
                </Route>
              </Route>
              <Route
                path="services-management"
                element={
                  <Protected permCheck={["admin", "office"]}>
                    <ServicesManagement />
                  </Protected>
                }
              />
              <Route
                path="shortcuts"
                element={
                  <Protected>
                    <ShortcutsPage />
                  </Protected>
                }
              />
              <Route
                path="manage-users"
                element={
                  <Protected permCheck={"admin"}>
                    <ManageUsers />
                  </Protected>
                }
              />
              {/* Feature-tour progress per person (registry §36.10). The
                  server query is admin-gated too (tourReport). */}
              <Route
                path="walkthroughs"
                element={
                  <Protected permCheck={"admin"}>
                    <WalkthroughsPage />
                  </Protected>
                }
              />
              <Route
                path="keap-merge-controller"
                element={
                  <Protected permCheck={"admin"}>
                    <React.Suspense>
                      <KeapMergeController />
                    </React.Suspense>
                  </Protected>
                }
              />
              <Route
                path="keap-file-merge"
                element={
                  <Protected permCheck={"admin"}>
                    <React.Suspense>
                      <KeapFileMerge />
                    </React.Suspense>
                  </Protected>
                }
              />
              <Route
                path="user-hourly-work-sheets"
                element={
                  <Protected>
                    <TimesheetPage />
                  </Protected>
                }
              />
              <Route
                path="job-form-review"
                element={
                  <Protected permCheck={["admin", "office"]}>
                    <React.Suspense>
                      <JobFormReviewPage />
                    </React.Suspense>
                  </Protected>
                }
              />
              <Route
                path="referrals"
                element={
                  <Protected permCheck={["admin", "office", "developer"]}>
                    <React.Suspense>
                      <ReferralsPage />
                    </React.Suspense>
                  </Protected>
                }
              />
              <Route
                path="stuck-submissions"
                element={
                  <Protected permCheck={"developer"}>
                    <React.Suspense>
                      <StuckSubmissionsPage />
                    </React.Suspense>
                  </Protected>
                }
              />
              <Route
                path="job-options-setup"
                element={
                  <Protected permCheck={"admin"}>
                    <React.Suspense>
                      <JobOptionsSetupPage />
                    </React.Suspense>
                  </Protected>
                }
              />
              <Route
                path="referred-by-setup"
                element={
                  <Protected permCheck={"admin"}>
                    <React.Suspense>
                      <ReferredBySetupPage />
                    </React.Suspense>
                  </Protected>
                }
              />
              <Route
                path="business-ledger"
                element={
                  <Protected permCheck={"admin"}>
                    <React.Suspense>
                      <BusinessLedgerPage />
                    </React.Suspense>
                  </Protected>
                }
              />
              <Route
                path="payroll-report"
                element={
                  <Protected permCheck={"admin"}>
                    <React.Suspense>
                      <PayrollReportPage />
                    </React.Suspense>
                  </Protected>
                }
              />
              <Route
                path="defaults-setup"
                element={
                  <Protected permCheck={["admin", "office"]}>
                    <React.Suspense>
                      <DefaultsSetupPage />
                    </React.Suspense>
                  </Protected>
                }
              />
              {/* Quick Texts manager (2026-09-03) — standalone route mirroring
                  services-management's mount, but permCheck'd admin/office to
                  match the defaults-setup gate + the server mutation gates
                  (outbound message content). Also mounted as a Defaults
                  Setup tab. */}
              <Route
                path="quick-texts"
                element={
                  <Protected permCheck={["admin", "office"]}>
                    <React.Suspense>
                      <QuickTextManagement />
                    </React.Suspense>
                  </Protected>
                }
              />
              <Route
                path="fcm-tokens"
                element={
                  <Protected permCheck={"developer"}>
                    <React.Suspense>
                      <FcmTokenManager />
                    </React.Suspense>
                  </Protected>
                }
              />
              <Route
                path="style"
                element={
                  <Protected permCheck={"developer"}>
                    <React.Suspense>
                      <StyleGuidePage />
                    </React.Suspense>
                  </Protected>
                }
              />
              {/* Developer-gated during rollout; flip permCheck open (remove
                  or widen) once the classification flow is proven. */}
              <Route
                path="classification-review"
                element={
                  <Protected permCheck={"developer"}>
                    <React.Suspense>
                      <ClassificationReviewPage />
                    </React.Suspense>
                  </Protected>
                }
              />
              {/* 2026-10-05 — there is no Clients PAGE any more. It held only
                  a search field, a create button and a recents list, so it
                  became the navbar's Clients dropdown. Both index routes
                  redirect rather than 404: the bookmark break was accepted,
                  but landing somewhere useful is free. */}
              <Route path="clients">
                <Route
                  index={true}
                  element={<Navigate to="/bdr-office/tasks" replace />}
                />
                <Route path="view-client">
                  <Route
                    index={true}
                    element={<Navigate to="/bdr-office/tasks" replace />}
                  />
                  <Route
                    path=":clientId"
                    element={
                      <Protected permCheck={["admin", "office"]}>
                        <SingleClient />
                      </Protected>
                    }
                  />
                </Route>
              </Route>
            </Route>

            <Route
              path="*"
              element={
                PUBLIC_V2 ? (
                  <HomeV2 displayNav={setDisplayNav} changeStickyTab={changeStickyTab} changeFooter={changeFooter} preview={false} />
                ) : (
                  <Home />
                )
              }
            />
          </Routes>
        </>
        {adminIsLoggedIn ? null : stickyTab ? <QuoteStickyTab /> : null}
        {adminIsLoggedIn ? null : displayFooter ? <Footer /> : null}
      </TourProvider>
      </Router>
      </SSEProvider>
      </DriveTimePinProvider>
      </GlobalConfirmProvider>
      </GlobalSnackbarProvider>
    </ApolloProvider>
  );
}

export default App;
